/**
 * Unified attributes — persistence and the runtime hooks.
 *
 * Three jobs:
 *   1. CRUD for the declarations (unified_attributes, unified_attribute_bindings).
 *   2. Border-crossing hooks: every semantic import and excursion return calls
 *      `recordAndPropagateInbound`, which (a) upserts what the connected app
 *      said about each item into item_source_attributes — metadata at rest —
 *      and (b) applies bound values to the canonical item per the attribute's
 *      conflict policy, writing a ledger row for every hop.
 *   3. Outbound: `onItemFieldsChanged` (called from the items store) queues a
 *      hop per bidirectional binding; `runOutboundQueue` executes the queue
 *      through the existing change_events → dispatch transport for the fields
 *      each dispatcher can carry, and records everything else as skipped with
 *      the fidelity reason.
 *
 * Loop protection: an inbound apply arms a short-lived suppression for
 * (item, field) naming the originating connector, so the store change it
 * causes does not queue a hop back to that connector; the ledger echo_key
 * carries the same fact durably.
 *
 * Authority: declarations are admin-edited (RLS); inbound applies are
 * explicit-basis writes (user-declared maps); outbound runs only on an
 * explicit action. Nothing here calls an authority-transition RPC (§2.3a).
 */

import { getTypedSupabaseClient } from '../typedSupabaseClient';
import type { Database, Json } from '../../types/database.generated';
import { useItemsStore } from '../../stores/prioritization/itemsStore';
import { getCurrentWorkspaceId } from '../supabaseSync';
import { getCachedWorkspaceModules, isModuleEnabled } from '../workspaceModulesService';
import { getExternalIds, logChangeEvent } from '../connectors';
import { triggerDispatch } from '../syncEngine';
import type { SemanticObject } from './snapshot';
import {
  canonicalValueToItemPatch,
  echoKey,
  extractSourceAttributes,
  itemFieldChangesToCanonical,
  outboundCarriable,
  planInbound,
  readCanonicalValue,
  translateOutbound,
  type BindingSourceKind,
  type PropagationDirection,
  type PropagationStatus,
  type UnifiedAttribute,
  type UnifiedAttributeBinding,
  type UnifiedAttributeWithBindings,
  type UnifiedConflictPolicy,
  type UnifiedPropagationMode,
  type ValueMapEntry,
} from './unifiedAttributes';

type AttrRow = Database['public']['Tables']['unified_attributes']['Row'];
type BindingRow = Database['public']['Tables']['unified_attribute_bindings']['Row'];
type PropagationRow = Database['public']['Tables']['unified_attribute_propagations']['Row'];

const MODULE_KEY = 'semantic_connectors';

// ── Row mapping ───────────────────────────────────────────────────────────────

function toAttribute(row: AttrRow): UnifiedAttribute {
  return {
    id: row.id,
    workspaceId: row.workspace_id,
    canonicalFieldKey: row.canonical_field_key,
    name: row.name,
    description: row.description,
    propagation: row.propagation as UnifiedPropagationMode,
    conflictPolicy: row.conflict_policy as UnifiedConflictPolicy,
    active: row.active,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

function toBinding(row: BindingRow): UnifiedAttributeBinding {
  const valueMap = Array.isArray(row.value_map)
    ? (row.value_map as unknown[])
        .map((e) => e as Partial<ValueMapEntry>)
        .filter((e) => typeof e.externalValue === 'string' && typeof e.internalValue === 'string')
        .map((e) => ({ externalValue: e.externalValue!, internalValue: e.internalValue! }))
    : [];
  const defaults =
    row.outbound_defaults && typeof row.outbound_defaults === 'object' && !Array.isArray(row.outbound_defaults)
      ? Object.fromEntries(
          Object.entries(row.outbound_defaults as Record<string, unknown>).filter(([, v]) => typeof v === 'string'),
        ) as Record<string, string>
      : {};
  return {
    id: row.id,
    unifiedAttributeId: row.unified_attribute_id,
    workspaceId: row.workspace_id,
    connectorType: row.connector_type,
    sourceKind: row.source_kind as BindingSourceKind,
    sourceKey: row.source_key,
    valueMap,
    outboundDefaults: defaults,
    enabled: row.enabled,
  };
}

export interface PropagationRecord {
  id: string;
  workspaceId: string;
  unifiedAttributeId: string | null;
  bindingId: string | null;
  itemId: string;
  canonicalFieldKey: string;
  direction: PropagationDirection;
  connectorType: string;
  fromValue: unknown;
  toValue: unknown;
  status: PropagationStatus;
  reason: string | null;
  origin: string | null;
  createdAt: string;
  resolvedAt: string | null;
}

function toPropagation(row: PropagationRow): PropagationRecord {
  return {
    id: row.id,
    workspaceId: row.workspace_id,
    unifiedAttributeId: row.unified_attribute_id,
    bindingId: row.binding_id,
    itemId: row.item_id,
    canonicalFieldKey: row.canonical_field_key,
    direction: row.direction as PropagationDirection,
    connectorType: row.connector_type,
    fromValue: row.from_value,
    toValue: row.to_value,
    status: row.status as PropagationStatus,
    reason: row.reason,
    origin: row.origin,
    createdAt: row.created_at,
    resolvedAt: row.resolved_at,
  };
}

// ── Declarations: read ────────────────────────────────────────────────────────

export async function listUnifiedAttributes(
  workspaceId: string,
  opts: { includeInactive?: boolean } = {},
): Promise<UnifiedAttributeWithBindings[]> {
  const db = getTypedSupabaseClient();
  let q = db.from('unified_attributes').select('*').eq('workspace_id', workspaceId).order('name');
  if (!opts.includeInactive) q = q.eq('active', true);
  const [{ data: attrs, error: aErr }, { data: bindings, error: bErr }] = await Promise.all([
    q,
    db.from('unified_attribute_bindings').select('*').eq('workspace_id', workspaceId).order('connector_type'),
  ]);
  if (aErr) throw new Error(`Failed to load unified attributes: ${aErr.message}`);
  if (bErr) throw new Error(`Failed to load unified attribute bindings: ${bErr.message}`);
  const byAttr = new Map<string, UnifiedAttributeBinding[]>();
  for (const b of (bindings ?? []).map(toBinding)) {
    const list = byAttr.get(b.unifiedAttributeId) ?? [];
    list.push(b);
    byAttr.set(b.unifiedAttributeId, list);
  }
  return (attrs ?? []).map((row) => ({ ...toAttribute(row), bindings: byAttr.get(row.id) ?? [] }));
}

// A small per-workspace cache so the store hook and crossings do not re-query
// on every mutation. Invalidated by every write below.
const INDEX_TTL_MS = 60_000;
const indexCache = new Map<string, { at: number; attributes: UnifiedAttributeWithBindings[] }>();

async function getIndex(workspaceId: string): Promise<UnifiedAttributeWithBindings[]> {
  const hit = indexCache.get(workspaceId);
  if (hit && Date.now() - hit.at < INDEX_TTL_MS) return hit.attributes;
  const attributes = await listUnifiedAttributes(workspaceId);
  indexCache.set(workspaceId, { at: Date.now(), attributes });
  return attributes;
}

export function invalidateUnifiedAttributeIndex(workspaceId?: string): void {
  if (workspaceId) indexCache.delete(workspaceId);
  else indexCache.clear();
}

// ── Declarations: write ───────────────────────────────────────────────────────

export interface UnifiedAttributeInput {
  canonicalFieldKey: string;
  name: string;
  description?: string | null;
  propagation: UnifiedPropagationMode;
  conflictPolicy: UnifiedConflictPolicy;
  active?: boolean;
}

function friendlyError(message: string): string {
  if (/unified_attributes_one_per_field/i.test(message)) {
    return 'That canonical field already has a unified attribute in this workspace.';
  }
  if (/unified_attribute_bindings_one_per_source/i.test(message)) {
    return 'That connector source is already bound to this attribute.';
  }
  if (/row-level security/i.test(message)) return 'Only a workspace admin can change unified attributes.';
  return message;
}

export async function createUnifiedAttribute(workspaceId: string, input: UnifiedAttributeInput): Promise<UnifiedAttribute> {
  const db = getTypedSupabaseClient();
  const { data, error } = await db
    .from('unified_attributes')
    .insert({
      workspace_id: workspaceId,
      canonical_field_key: input.canonicalFieldKey,
      name: input.name.trim(),
      description: input.description?.trim() || null,
      propagation: input.propagation,
      conflict_policy: input.conflictPolicy,
      active: input.active ?? true,
    })
    .select('*')
    .single();
  if (error) throw new Error(friendlyError(error.message));
  invalidateUnifiedAttributeIndex(workspaceId);
  return toAttribute(data);
}

export async function updateUnifiedAttribute(id: string, patch: Partial<UnifiedAttributeInput>): Promise<UnifiedAttribute> {
  const db = getTypedSupabaseClient();
  const update: Database['public']['Tables']['unified_attributes']['Update'] = {};
  if (patch.canonicalFieldKey !== undefined) update.canonical_field_key = patch.canonicalFieldKey;
  if (patch.name !== undefined) update.name = patch.name.trim();
  if (patch.description !== undefined) update.description = patch.description?.trim() || null;
  if (patch.propagation !== undefined) update.propagation = patch.propagation;
  if (patch.conflictPolicy !== undefined) update.conflict_policy = patch.conflictPolicy;
  if (patch.active !== undefined) update.active = patch.active;
  const { data, error } = await db.from('unified_attributes').update(update).eq('id', id).select('*').single();
  if (error) throw new Error(friendlyError(error.message));
  invalidateUnifiedAttributeIndex(data.workspace_id);
  return toAttribute(data);
}

export async function deleteUnifiedAttribute(id: string, workspaceId: string): Promise<void> {
  const db = getTypedSupabaseClient();
  const { error } = await db.from('unified_attributes').delete().eq('id', id);
  if (error) throw new Error(friendlyError(error.message));
  invalidateUnifiedAttributeIndex(workspaceId);
}

export interface BindingInput {
  connectorType: string;
  sourceKind: BindingSourceKind;
  sourceKey: string;
  valueMap: ValueMapEntry[];
  outboundDefaults: Record<string, string>;
  enabled?: boolean;
}

export async function createBinding(
  workspaceId: string,
  unifiedAttributeId: string,
  input: BindingInput,
): Promise<UnifiedAttributeBinding> {
  const db = getTypedSupabaseClient();
  const { data, error } = await db
    .from('unified_attribute_bindings')
    .insert({
      workspace_id: workspaceId,
      unified_attribute_id: unifiedAttributeId,
      connector_type: input.connectorType,
      source_kind: input.sourceKind,
      source_key: input.sourceKey.trim(),
      value_map: input.valueMap as unknown as NonNullable<Json>,
      outbound_defaults: input.outboundDefaults as unknown as NonNullable<Json>,
      enabled: input.enabled ?? true,
    })
    .select('*')
    .single();
  if (error) throw new Error(friendlyError(error.message));
  invalidateUnifiedAttributeIndex(workspaceId);
  return toBinding(data);
}

export async function updateBinding(id: string, workspaceId: string, patch: Partial<BindingInput>): Promise<UnifiedAttributeBinding> {
  const db = getTypedSupabaseClient();
  const update: Database['public']['Tables']['unified_attribute_bindings']['Update'] = {};
  if (patch.connectorType !== undefined) update.connector_type = patch.connectorType;
  if (patch.sourceKind !== undefined) update.source_kind = patch.sourceKind;
  if (patch.sourceKey !== undefined) update.source_key = patch.sourceKey.trim();
  if (patch.valueMap !== undefined) update.value_map = patch.valueMap as unknown as NonNullable<Json>;
  if (patch.outboundDefaults !== undefined) update.outbound_defaults = patch.outboundDefaults as unknown as NonNullable<Json>;
  if (patch.enabled !== undefined) update.enabled = patch.enabled;
  const { data, error } = await db.from('unified_attribute_bindings').update(update).eq('id', id).select('*').single();
  if (error) throw new Error(friendlyError(error.message));
  invalidateUnifiedAttributeIndex(workspaceId);
  return toBinding(data);
}

export async function deleteBinding(id: string, workspaceId: string): Promise<void> {
  const db = getTypedSupabaseClient();
  const { error } = await db.from('unified_attribute_bindings').delete().eq('id', id);
  if (error) throw new Error(friendlyError(error.message));
  invalidateUnifiedAttributeIndex(workspaceId);
}

// ── Ledger ────────────────────────────────────────────────────────────────────

interface LedgerInsert {
  workspaceId: string;
  unifiedAttributeId: string | null;
  bindingId: string | null;
  itemId: string;
  canonicalFieldKey: string;
  direction: PropagationDirection;
  connectorType: string;
  fromValue?: unknown;
  toValue?: unknown;
  status: PropagationStatus;
  reason?: string;
  echoKey?: string;
  origin?: string;
}

async function insertLedger(rows: LedgerInsert[]): Promise<void> {
  if (rows.length === 0) return;
  const db = getTypedSupabaseClient();
  const { error } = await db.from('unified_attribute_propagations').insert(
    rows.map((r) => ({
      workspace_id: r.workspaceId,
      unified_attribute_id: r.unifiedAttributeId,
      binding_id: r.bindingId,
      item_id: r.itemId,
      canonical_field_key: r.canonicalFieldKey,
      direction: r.direction,
      connector_type: r.connectorType,
      from_value: (r.fromValue ?? null) as Json,
      to_value: (r.toValue ?? null) as Json,
      status: r.status,
      reason: r.reason ?? null,
      echo_key: r.echoKey ?? null,
      origin: r.origin ?? null,
    })),
  );
  if (error) console.warn('[unifiedAttributes] ledger insert failed:', error.message);
}

export async function listPropagations(
  workspaceId: string,
  opts: { limit?: number; status?: PropagationStatus[] } = {},
): Promise<PropagationRecord[]> {
  const db = getTypedSupabaseClient();
  let q = db
    .from('unified_attribute_propagations')
    .select('*')
    .eq('workspace_id', workspaceId)
    .order('created_at', { ascending: false })
    .limit(opts.limit ?? 50);
  if (opts.status && opts.status.length > 0) q = q.in('status', opts.status);
  const { data, error } = await q;
  if (error) throw new Error(`Failed to load propagation ledger: ${error.message}`);
  return (data ?? []).map(toPropagation);
}

export async function countPropagations(workspaceId: string): Promise<{ queued: number; review: number }> {
  const rows = await listPropagations(workspaceId, { limit: 500, status: ['queued', 'review'] });
  return {
    queued: rows.filter((r) => r.status === 'queued').length,
    review: rows.filter((r) => r.status === 'review').length,
  };
}

// ── Loop protection ───────────────────────────────────────────────────────────

const SUPPRESS_MS = 15_000;
const suppressions = new Map<string, { connectorType: string; until: number }>();

function armSuppression(itemId: string, canonicalKey: string, connectorType: string): void {
  suppressions.set(`${itemId}::${canonicalKey}`, { connectorType, until: Date.now() + SUPPRESS_MS });
}

function suppressedConnector(itemId: string, canonicalKey: string): string | null {
  const hit = suppressions.get(`${itemId}::${canonicalKey}`);
  if (!hit) return null;
  if (hit.until < Date.now()) {
    suppressions.delete(`${itemId}::${canonicalKey}`);
    return null;
  }
  return hit.connectorType;
}

function moduleOn(workspaceId: string): boolean {
  // Best effort: the shared cache is only for the session's current
  // workspace; other workspaces fall through to "on" and rely on the fact
  // that declarations can only exist where an admin created them.
  if (workspaceId !== getCurrentWorkspaceId()) return true;
  return isModuleEnabled(getCachedWorkspaceModules(), MODULE_KEY);
}

// ── Border crossings: record at rest + propagate inbound ──────────────────────

export interface CrossingInput {
  workspaceId: string;
  connectorType: string;
  origin: 'import' | 'excursion_return' | 'sync';
  objects: Array<{ object: SemanticObject; itemId: string }>;
  /** Items currently inside an open excursion's return review are recorded but not written (doc 07 §5 Q5). */
  holdItemIds?: ReadonlySet<string>;
  userId?: string;
}

export interface CrossingResult {
  recorded: number;
  applied: number;
  held: number;
  skipped: number;
  errors: string[];
}

/** One thing a connected app said about one item. */
export interface SourceAttributeInput {
  itemId: string;
  /** The object's id in that app, when known. */
  externalId: string | null;
  sourceKind: BindingSourceKind;
  sourceKey: string;
  rawValue: unknown;
}

/**
 * Metadata at rest: upsert what a connected app said about each item into
 * item_source_attributes (one row per item, connector, kind and key — the
 * latest crossing wins). The one writer of that table. Never throws: the
 * crossing that calls it must not fail because this could not be recorded.
 */
export async function recordSourceAttributes(input: {
  workspaceId: string;
  connectorType: string;
  origin: CrossingInput['origin'];
  rows: SourceAttributeInput[];
}): Promise<{ recorded: number; error?: string }> {
  if (input.rows.length === 0) return { recorded: 0 };
  try {
    const observedAt = new Date().toISOString();
    const { error } = await getTypedSupabaseClient()
      .from('item_source_attributes')
      .upsert(
        input.rows.map((r) => ({
          workspace_id: input.workspaceId,
          item_id: r.itemId,
          connector_type: input.connectorType,
          external_id: r.externalId,
          source_kind: r.sourceKind,
          source_key: r.sourceKey,
          raw_value: (r.rawValue ?? null) as Json,
          origin: input.origin,
          observed_at: observedAt,
        })),
        { onConflict: 'item_id,connector_type,source_kind,source_key' },
      );
    if (error) return { recorded: 0, error: `Source attributes not recorded: ${error.message}` };
    return { recorded: input.rows.length };
  } catch (err) {
    return { recorded: 0, error: `Source attributes not recorded: ${err instanceof Error ? err.message : 'unknown'}` };
  }
}

/**
 * Called by every semantic import and excursion return after items exist.
 * Never throws: a crossing must not fail because unification could not be
 * recorded — failures come back in `errors`.
 */
export async function recordAndPropagateInbound(input: CrossingInput): Promise<CrossingResult> {
  const result: CrossingResult = { recorded: 0, applied: 0, held: 0, skipped: 0, errors: [] };
  if (input.objects.length === 0 || !moduleOn(input.workspaceId)) return result;

  // 1. Metadata at rest — every attribute every object carried.
  const recorded = await recordSourceAttributes({
    workspaceId: input.workspaceId,
    connectorType: input.connectorType,
    origin: input.origin,
    rows: input.objects.flatMap(({ object, itemId }) =>
      extractSourceAttributes(object).map((a) => ({ itemId, externalId: object.externalId, ...a })),
    ),
  });
  result.recorded = recorded.recorded;
  if (recorded.error) result.errors.push(recorded.error);

  // 2. Bound values → canonical item.
  let attributes: UnifiedAttributeWithBindings[];
  try {
    attributes = await getIndex(input.workspaceId);
  } catch (err) {
    result.errors.push(err instanceof Error ? err.message : 'Failed to load unified attributes');
    return result;
  }
  if (attributes.length === 0) return result;

  const itemsStore = useItemsStore.getState();
  const plan = planInbound({
    connectorType: input.connectorType,
    objects: input.objects,
    attributes,
    currentValue: (itemId, key) => {
      const item = itemsStore.getItemById(itemId);
      return item ? readCanonicalValue(item, key) : undefined;
    },
  });

  const ledger: LedgerInsert[] = [];
  for (const row of plan) {
    if (row.action === 'noop') continue;
    const base: LedgerInsert = {
      workspaceId: input.workspaceId,
      unifiedAttributeId: row.unifiedAttributeId,
      bindingId: row.bindingId,
      itemId: row.itemId,
      canonicalFieldKey: row.canonicalKey,
      direction: 'inbound',
      connectorType: row.connectorType,
      fromValue: row.currentValue,
      toValue: row.value,
      status: 'skipped',
      reason: row.reason,
      echoKey: echoKey(row.itemId, row.canonicalKey, row.value),
      origin: input.origin,
    };
    if (row.action === 'skip') {
      result.skipped++;
      ledger.push(base);
      continue;
    }
    if (row.action === 'review' || input.holdItemIds?.has(row.itemId)) {
      result.held++;
      ledger.push({
        ...base,
        status: 'review',
        reason: input.holdItemIds?.has(row.itemId) ? 'item is inside an open excursion review' : row.reason,
      });
      continue;
    }
    const item = itemsStore.getItemById(row.itemId);
    const patch = canonicalValueToItemPatch(row.canonicalKey, row.value, item?.metadata);
    if (!patch) {
      result.skipped++;
      ledger.push({ ...base, reason: `"${String(row.value)}" cannot be written to ${row.canonicalKey}` });
      continue;
    }
    try {
      armSuppression(row.itemId, row.canonicalKey, row.connectorType);
      itemsStore.updateItem(row.itemId, patch as Parameters<typeof itemsStore.updateItem>[1]);
      result.applied++;
      ledger.push({ ...base, status: 'applied', reason: undefined });
    } catch (err) {
      result.errors.push(`Apply failed for ${row.canonicalKey} on ${row.itemId}: ${err instanceof Error ? err.message : 'unknown'}`);
      ledger.push({ ...base, status: 'failed', reason: err instanceof Error ? err.message : 'apply failed' });
    }
  }
  await insertLedger(ledger);
  return result;
}

// ── Outbound: queue on canonical change ───────────────────────────────────────

/**
 * Items-store hook. Fire-and-forget; called with the store's field-change map.
 * Queues one hop per enabled binding of every bidirectional attribute whose
 * canonical field changed — except the connector whose inbound hop caused
 * this very change (echo suppression).
 */
export async function onItemFieldsChanged(params: {
  workspaceId: string | null | undefined;
  itemId: string;
  fieldChanges: Record<string, { from: unknown; to: unknown }>;
}): Promise<void> {
  const workspaceId = params.workspaceId ?? getCurrentWorkspaceId();
  if (!workspaceId || !moduleOn(workspaceId)) return;
  const changes = itemFieldChangesToCanonical(params.fieldChanges);
  if (changes.length === 0) return;
  const attributes = await getIndex(workspaceId).catch(() => [] as UnifiedAttributeWithBindings[]);
  if (attributes.length === 0) return;

  const ledger: LedgerInsert[] = [];
  for (const change of changes) {
    const attr = attributes.find((a) => a.active && a.propagation === 'bidirectional' && a.canonicalFieldKey === change.canonicalKey);
    if (!attr) continue;
    const origin = suppressedConnector(params.itemId, change.canonicalKey);
    for (const binding of attr.bindings) {
      if (!binding.enabled) continue;
      if (origin && binding.connectorType === origin) continue;
      ledger.push({
        workspaceId,
        unifiedAttributeId: attr.id,
        bindingId: binding.id,
        itemId: params.itemId,
        canonicalFieldKey: change.canonicalKey,
        direction: 'outbound',
        connectorType: binding.connectorType,
        fromValue: change.from,
        toValue: change.to,
        status: 'queued',
        echoKey: echoKey(params.itemId, change.canonicalKey, change.to),
        origin: 'item_update',
      });
    }
  }
  await insertLedger(ledger);
}

// ── Outbound: execute the queue ───────────────────────────────────────────────

export interface OutboundRunResult {
  dispatched: number;
  skipped: number;
  failed: number;
  dispatchError: string | null;
}

/**
 * Execute queued hops. Each becomes a change_events row (source
 * 'priorities_ai') that the dispatch function carries to the connector —
 * when that dispatcher can carry the field. Everything else is recorded as
 * skipped with the reason. Explicit action, never unattended.
 */
export async function runOutboundQueue(workspaceId: string): Promise<OutboundRunResult> {
  const result: OutboundRunResult = { dispatched: 0, skipped: 0, failed: 0, dispatchError: null };
  const db = getTypedSupabaseClient();
  const queued = await listPropagations(workspaceId, { limit: 200, status: ['queued'] });
  if (queued.length === 0) return result;
  const attributes = await getIndex(workspaceId);
  const bindingsById = new Map(attributes.flatMap((a) => a.bindings.map((b) => [b.id, b] as const)));
  const externalIdCache = new Map<string, Awaited<ReturnType<typeof getExternalIds>>>();

  const settle = async (id: string, status: PropagationStatus, reason?: string) => {
    await db
      .from('unified_attribute_propagations')
      .update({ status, reason: reason ?? null, resolved_at: new Date().toISOString() })
      .eq('id', id);
  };

  for (const hop of queued) {
    const binding = hop.bindingId ? bindingsById.get(hop.bindingId) : undefined;
    if (!binding) {
      await settle(hop.id, 'skipped', 'binding no longer exists');
      result.skipped++;
      continue;
    }
    const carriable = outboundCarriable(binding.connectorType, hop.canonicalFieldKey);
    if (!carriable.ok) {
      await settle(hop.id, 'skipped', carriable.reason);
      result.skipped++;
      continue;
    }
    let ids = externalIdCache.get(hop.itemId);
    if (!ids) {
      ids = await getExternalIds(workspaceId, 'item', hop.itemId);
      externalIdCache.set(hop.itemId, ids);
    }
    if (!ids.some((x) => x.connectorType === binding.connectorType)) {
      await settle(hop.id, 'skipped', `item has no identity in ${binding.connectorType}`);
      result.skipped++;
      continue;
    }
    const translated = translateOutbound(binding, hop.toValue);
    if (!translated.ok) {
      await settle(hop.id, 'skipped', `lossy: ${translated.reason}`);
      result.skipped++;
      continue;
    }
    try {
      await logChangeEvent({
        workspaceId,
        entityType: 'item',
        entityId: hop.itemId,
        eventType: 'updated',
        fieldName: hop.canonicalFieldKey,
        oldValue: hop.fromValue,
        newValue: translated.value,
      });
      await settle(hop.id, 'dispatched', `queued to ${binding.connectorType} via sync dispatch`);
      result.dispatched++;
    } catch (err) {
      await settle(hop.id, 'failed', err instanceof Error ? err.message : 'change event failed');
      result.failed++;
    }
  }

  if (result.dispatched > 0) {
    const { error } = await triggerDispatch({ workspaceId });
    result.dispatchError = error;
  }
  return result;
}

// ── Review queue ──────────────────────────────────────────────────────────────

export async function resolveReview(record: PropagationRecord, decision: 'apply' | 'dismiss'): Promise<void> {
  const db = getTypedSupabaseClient();
  if (decision === 'dismiss') {
    await db
      .from('unified_attribute_propagations')
      .update({ status: 'dismissed', resolved_at: new Date().toISOString() })
      .eq('id', record.id);
    return;
  }
  const itemsStore = useItemsStore.getState();
  const item = itemsStore.getItemById(record.itemId);
  const patch = canonicalValueToItemPatch(record.canonicalFieldKey, record.toValue, item?.metadata);
  if (!patch) {
    await db
      .from('unified_attribute_propagations')
      .update({ status: 'skipped', reason: 'value cannot be written', resolved_at: new Date().toISOString() })
      .eq('id', record.id);
    return;
  }
  armSuppression(record.itemId, record.canonicalFieldKey, record.connectorType);
  itemsStore.updateItem(record.itemId, patch as Parameters<typeof itemsStore.updateItem>[1]);
  await db
    .from('unified_attribute_propagations')
    .update({ status: 'applied', reason: 'applied from review', resolved_at: new Date().toISOString() })
    .eq('id', record.id);
}

// ── At-rest inspection ────────────────────────────────────────────────────────

export interface SourceAttributeRecord {
  itemId: string;
  connectorType: string;
  externalId: string | null;
  sourceKind: BindingSourceKind;
  sourceKey: string;
  rawValue: unknown;
  origin: string;
  observedAt: string;
}

export async function listSourceAttributesForItem(workspaceId: string, itemId: string): Promise<SourceAttributeRecord[]> {
  return listSourceAttributes(workspaceId, { itemId });
}

const SOURCE_ATTRIBUTE_PAGE = 1000;

/** What was recorded in this workspace — for one item, one connector, or both — every page of it. */
export async function listSourceAttributes(
  workspaceId: string,
  filter: { itemId?: string; connectorType?: string } = {},
): Promise<SourceAttributeRecord[]> {
  const db = getTypedSupabaseClient();
  const out: SourceAttributeRecord[] = [];
  for (let from = 0; ; from += SOURCE_ATTRIBUTE_PAGE) {
    let q = db.from('item_source_attributes').select('*').eq('workspace_id', workspaceId);
    if (filter.itemId) q = q.eq('item_id', filter.itemId);
    if (filter.connectorType) q = q.eq('connector_type', filter.connectorType);
    const { data, error } = await q
      .order('connector_type')
      .order('source_key')
      .order('id')
      .range(from, from + SOURCE_ATTRIBUTE_PAGE - 1);
    if (error) throw new Error(`Failed to load source attributes: ${error.message}`);
    for (const r of data ?? []) {
      out.push({
        itemId: r.item_id,
        connectorType: r.connector_type,
        externalId: r.external_id,
        sourceKind: r.source_kind as BindingSourceKind,
        sourceKey: r.source_key,
        rawValue: r.raw_value,
        origin: r.origin,
        observedAt: r.observed_at,
      });
    }
    if ((data ?? []).length < SOURCE_ATTRIBUTE_PAGE) return out;
  }
}

/** Distinct source keys a connector has ever reported in this workspace — feeds the binding picker. */
export async function listObservedSourceKeys(
  workspaceId: string,
  connectorType: string,
): Promise<Array<{ sourceKind: BindingSourceKind; sourceKey: string; sampleValues: string[] }>> {
  const db = getTypedSupabaseClient();
  const { data, error } = await db
    .from('item_source_attributes')
    .select('source_kind, source_key, raw_value')
    .eq('workspace_id', workspaceId)
    .eq('connector_type', connectorType)
    .limit(2000);
  if (error) throw new Error(`Failed to load observed source keys: ${error.message}`);
  const acc = new Map<string, { sourceKind: BindingSourceKind; sourceKey: string; samples: Set<string> }>();
  for (const r of data ?? []) {
    const k = `${r.source_kind}:${r.source_key}`;
    const entry = acc.get(k) ?? { sourceKind: r.source_kind as BindingSourceKind, sourceKey: r.source_key, samples: new Set<string>() };
    if (r.raw_value !== null && r.raw_value !== undefined && entry.samples.size < 8) entry.samples.add(String(r.raw_value));
    acc.set(k, entry);
  }
  return [...acc.values()].map((e) => ({ sourceKind: e.sourceKind, sourceKey: e.sourceKey, sampleValues: [...e.samples] }));
}
